import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

const ManageEvents = () => {
  const [events, setEvents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        onConfirm: null,
    });

    const truncateText = (text, limit = 30) =>
        text && text.length > limit ? text.substring(0, limit) + '…' : text;

    const renderPagination = () => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const last = pagination.last_page;
        const current = pagination.current_page;

        return (
            <nav className="mt-4">
                <ul className="pagination justify-content-center">

                    {/* Previous */}
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => fetchEvents(current - 1)}
                        >
                            Previous
                        </button>
                    </li>

                    {/* Page 1 */}
                    <li className={`page-item ${current === 1 ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => fetchEvents(1)}>
                            1
                        </button>
                    </li>

                    {/* Page 2 (only if more than 1 page) */}
                    {last >= 2 && (
                        <li className={`page-item ${current === 2 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => fetchEvents(2)}>
                                2
                            </button>
                        </li>
                    )}

                    {/* Ellipsis (only if more than 3 pages) */}
                    {last > 3 && (
                        <li className="page-item disabled">
                            <span className="page-link">…</span>
                        </li>
                    )}

                    {/* Last page (avoid duplicate when last === 1 or 2) */}
                    {last > 2 && (
                        <li className={`page-item ${current === last ? 'active' : ''}`}>
                            <button
                                className="page-link"
                                onClick={() => fetchEvents(last)}
                            >
                                {last}
                            </button>
                        </li>
                    )}

                    {/* Next */}
                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button
                            className="page-link"
                            onClick={() => fetchEvents(current + 1)}
                        >
                            Next
                        </button>
                    </li>

                </ul>
            </nav>
        );
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 800);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        fetchEvents(1); // reset to first page
    }, [debouncedSearch, categoryFilter ,statusFilter]);

    useEffect(() => {
        fetchCategories();
    }, []);


    const fetchEvents = async (pageNumber = 1) => {
        try {
            setLoading(true);

            const params = new URLSearchParams();
            params.append('page', pageNumber);

            if (debouncedSearch) params.append('search', debouncedSearch);
            if (categoryFilter !== 'all') params.append('category_id', categoryFilter);
            if (statusFilter !== 'all') {
                params.append('status', statusFilter); // "active" or "inactive"
            }

            const response = await api.get(`admin/events/all?${params.toString()}`);

            const paginator = response.data.data;

            setEvents(paginator.data || []);
            setPagination(paginator);
            setPage(pageNumber);
        } catch (error) {
            toast.error('Failed to load events');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };


    const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.data || response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

    const handleStatusToggle = async (event) => {
        const isActive = event.status === 1 || event.status === true;
        const action = isActive ? 'Deactivate' : 'Activate';

        const result = await Swal.fire({
            title: `${action} Event`,
            text: `Are you sure you want to ${action.toLowerCase()} this event?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: action,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.put(`/admin/events/${event.id}/status`, {
                status: isActive ? 0 : 1,
            });
            toast.success(`Event ${isActive ? 'deactivated' : 'activated'} successfully`);
            fetchEvents(page);
        } catch {
            toast.error('Failed to update event status');
        }
    };

    const handleNotificationToggle = async (event) => {
        // Ensure we have a boolean
        const enabled = event.organizer_notification?.notifications_enabled || false // strictly boolean
        const action = enabled ? 'Disable' : 'Enable';

        const result = await Swal.fire({
            title: `${action} Notifications`,
            text: `${action} notifications for this event?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: action,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            // Send the opposite value
            await api.put(`/admin/events/${event.id}/notifications`, {
                notifications_enabled: enabled ? 0 : 1, // backend accepts 0/1
            });

            toast.success(`Notifications ${enabled ? 'disabled' : 'enabled'}`);

            // Refresh the events list so we get the updated boolean from backend
            fetchEvents(page);
        } catch (error) {
            toast.error('Failed to update notifications');
            console.error(error);
        }
    };


    const handleFeatureToggle = async (event) => {
        const isFeatured = event.featured_event ? true : false;
        const action = isFeatured ? 'Remove Featured' : 'Make Featured';

        const result = await Swal.fire({
            title: action,
            text: isFeatured
                ? 'Remove this event from featured?'
                : 'Make this event featured?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: action,
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.put(`/admin/events/${event.id}/featured`, {
                featured_event: isFeatured ? 0 : 1,
            });
            toast.success(isFeatured ? 'Removed from featured' : 'Marked as featured');
            fetchEvents(page);
        } catch {
            toast.error('Failed to update featured status');
        }
    };

    const handleDelete = async (eventId) => {
        const result = await Swal.fire({
            title: 'Delete Event',
            text: 'Are you sure you want to delete this event? This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Delete',
            cancelButtonText: 'Cancel',
        });

        if (!result.isConfirmed) return;

        try {
            await api.delete(`/admin/events/${eventId}`);
            toast.success('Event deleted successfully');
            fetchEvents(page);
        } catch {
            toast.error('Failed to delete event');
        }
    };





    const handleLoginAsOrganizer = async (organizerId) => {
    try {
      const response = await api.post('/admin/login-as-organizer', { organizer_id: organizerId });
      console.log('Login response:', response.data);
      if (response.data.success) {
        // Store current admin session for returning later
        const currentToken = localStorage.getItem('token');
        const currentUser = localStorage.getItem('user');
        localStorage.setItem('admin_return_token', currentToken);
        localStorage.setItem('admin_return_user', currentUser);
        localStorage.setItem('admin_impersonating', 'true');
        
        // Switch to organizer session
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.organizer));
        localStorage.setItem('userRole', 'organizer');
        
        toast.success('Logged in as organizer');
        // Redirect to organizer dashboard
        window.location.href = '/organizer/dashboard';
      }
    } catch (error) {
      toast.error('Failed to login as organizer');
      console.error('Error:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };



  return (
    <div className="container-fluid">
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 font-weight-bold text-gray-900">Manage Events</h1>
      </div>

      {/* Filters */}
      <div className="card shadow mb-4">
        <div className="card-body">
          <div className="row">
            <div className="col-md-4">
              <input
                type="text"
                className="form-control"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <select
                className="form-control"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="col-md-3">
              <select
                className="form-control"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Events Table */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-gray-900">All Events ({pagination.total || events.length})</h6>
        </div>
        <div className="card-body">
            {loading ? (
                <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            ) : (
          <div className="table-responsive">
            <table className="table table-bordered" width="100%" cellSpacing="0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Title</th>
                  <th>Organizer</th>
                  <th>Category</th>
                  <th>Location</th>
                  <th>Date</th>
                    <th>Currency</th>
                  <th>Status</th>
                  <th>Featured</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.length > 0 ? (
                  events.map(event => (
                    <tr key={event.id}>
                      <td>{event.id}</td>
                      <td>
                        <Link to={`/events/${event.id}`} className="text-primary font-weight-bold">
                            {truncateText(event.title, 30)}
                        </Link>
                      </td>
                      <td>{event.organizer?.name || 'N/A'}</td>
                      <td>
                        {event.category ? (
                          <span className="badge badge-primary">
                            {event.category.icon} {event.category.category_name}
                          </span>
                        ) : 'N/A'}
                      </td>
                      <td>{truncateText(event.location, 30)}</td>
                      <td>{formatDate(event.event_start)}</td>
                        <td>{event.currency}</td>
                      <td>
                        <span className={`badge ${(event.status === 1 || event.status === true) ? 'badge-success' : 'badge-danger'}`}>
                          {(event.status === 1 || event.status === true) ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                        <td>
                            <div className="d-flex gap-1 flex-wrap">
                                {event.featured_event && (
                                    <span className="badge badge-warning">
                                        <i className="fas fa-star mr-1"></i>
                                        Featured
                                      </span>
                                )}

                                {event.top_event && (
                                    <span className="badge badge-primary">
                                        <i className="fas fa-fire mr-1"></i>
                                        Top
                                      </span>
                                )}

                                {!event.featured_event && !event.top_event && (
                                    <span className="badge badge-secondary">
                                        Regular
                                      </span>
                                )}
                            </div>
                        </td>
                      <td>
                        <div className="d-flex gap-2">
                          <Link
                            to={`/events/${event.id}`}
                            className="p-2 text-success rounded transition-colors text-decoration-none border-0 bg-transparent"
                            style={{ background: 'transparent' }}
                            title="View Event"
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#d1e7dd'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </Link>
                          {event.organizer?.id && (
                            <button
                              onClick={() => handleLoginAsOrganizer(event.organizer.id)}
                              className="p-2 text-primary rounded transition-colors border-0 bg-transparent"
                              style={{ background: 'transparent' }}
                              title="Login as Organizer"
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#cfe2ff'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                              </svg>
                            </button>
                          )}
                          {/*  <button*/}
                          {/*      onClick={() => handleFeatureToggle(event)}*/}
                          {/*      className={`p-2 ${*/}
                          {/*          event.featured_event ? 'text-warning' : 'text-secondary'*/}
                          {/*      } rounded border-0 bg-transparent`}*/}
                          {/*  >*/}
                          {/*  <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill={event.featured_event ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">*/}
                          {/*    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />*/}
                          {/*  </svg>*/}
                          {/*</button>*/}
                            {(() => {
                                const notificationsEnabled = event.organizer_notification?.notifications_enabled || false;
                                return (
                                    <button
                                        onClick={() => handleNotificationToggle(event)}
                                        className={`p-2 rounded border-0 bg-transparent ${notificationsEnabled ? 'text-success' : 'text-secondary'}`}
                                        title={notificationsEnabled ? 'Disable Notifications' : 'Enable Notifications'}
                                    >
                                        {notificationsEnabled ? (
                                            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M10 2a6 6 0 00-6 6v3.586L2.707 13.88A1 1 0 003.414 15h13.172a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6z"/>
                                                <path d="M9 18a1 1 0 002 0"/>
                                            </svg>
                                        ) : (
                                            <svg style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                      d="M13.73 21a2 2 0 01-3.46 0M18.63 13A17.89 17.89 0 0118 8a6 6 0 00-9.33-5M6.26 6.26A5.97 5.97 0 006 8c0 3.18-.5 5-1.63 6.13M3 3l18 18"/>
                                            </svg>
                                        )}
                                    </button>
                                );
                            })()}

                            <button
                                onClick={() => handleStatusToggle(event)}
                                className={`p-2 ${
                                    (event.status === 1 || event.status === true)
                                        ? 'text-warning'
                                        : 'text-success'
                                } rounded border-0 bg-transparent`}
                            >
                            {(event.status === 1 || event.status === true) ? (
                              <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                            ) : (
                              <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                          </button>
                          <button
                              onClick={() => handleDelete(event.id)}
                            className="p-2 text-danger rounded transition-colors border-0 bg-transparent"
                            style={{ background: 'transparent' }}
                            title="Delete"
                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8d7da'}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <svg className="w-5 h-5" style={{ width: '1.25rem', height: '1.25rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9" className="text-center">No events found</td>
                  </tr>
                )}
              </tbody>
            </table>
              {renderPagination()}
          </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default ManageEvents;

